import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { deserializeValue, serializeValue } from '@/utils/filterUtils';

export default function useQueryParams(defaultParams = {}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const setQueryParams = useCallback(
    (newParams) => {
      const currentParams = Object.fromEntries(searchParams.entries());

      const updatedParams = {
        ...defaultParams,
        ...currentParams,
        ...newParams,
      };

      Object.keys(updatedParams).forEach((key) => {
        if (updatedParams[key] === undefined || updatedParams[key] === null) {
          delete updatedParams[key];
        } else {
          updatedParams[key] = serializeValue(updatedParams[key]);
        }
      });

      setSearchParams(updatedParams);
    },
    [defaultParams, searchParams, setSearchParams]
  );

  const value = useMemo(() => {
    const rawParams = Object.fromEntries(
      Array.from(searchParams.entries()).map(([key, value]) => [
        key,
        deserializeValue(value),
      ])
    );

    const filteredParams = Object.fromEntries(
      Object.entries(rawParams).filter(([_, value]) => value !== undefined)
    );

    return {
      queryParams: {
        ...defaultParams,
        ...rawParams,
      },
      queryParamsString: new URLSearchParams(filteredParams).toString(),
      setQueryParams,
    };
  }, [defaultParams, searchParams, setQueryParams]);

  return value;
}
